'use client';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import { debounce } from '@eliadmin/core/src/hooks/use-debounce';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import {
  endOfWeek,
  startOfWeek,
  format,
  addDays,
  startOfDay,
  isBefore,
  isWeekend,
  differenceInCalendarDays,
} from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { Heading } from '../ui/heading';
import { Button } from '../ui/button';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';

import { ReservationDatepicker } from '@eliadmin/core/src/components/reservation/reservation-calendar/reservation-datepicker';
import {
  fetchReservationSlots,
  type ReservationSlot,
  ReservationSlotType,
} from '@eliadmin/core/src/api/reservation/reservation-api';
import { getHolidays, type AvailableLocales } from '@eliadmin/core/src/utils/date-holidays-utils';
import {
  normalizeConfiguredDate,
  specificDatesConfiguration,
} from '../reservation/reservation-calendar/specific-dates';

export function FreeSlots({ weekStartsOn = 1 }: { weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6 }) {
  const { t } = useTranslations();
  const reservationPersonId = '3';
  const reservationServiceId = '1';

  const locale = useLocaleStore(s => s.currentLocale);
  const today = useMemo(() => startOfDay(new Date()), []);
  const holidayKeySet = useMemo(() => {
    try {
      const holidayDates = getHolidays(locale as AvailableLocales);
      return new Set(holidayDates.map(d => format(startOfDay(d), 'yyyy-MM-dd')));
    } catch {
      return new Set<string>();
    }
  }, [locale]);
  const alwaysOpenKeySet = useMemo(() => {
    return new Set(
      (specificDatesConfiguration.always_open ?? [])
        .map(normalizeConfiguredDate)
        .filter((value): value is string => value !== null),
    );
  }, []);
  const alwaysClosedKeySet = useMemo(() => {
    return new Set(
      (specificDatesConfiguration.always_closed ?? [])
        .map(normalizeConfiguredDate)
        .filter((value): value is string => value !== null),
    );
  }, []);

  const [dateRange, setDateRange] = useState(() => {
    const baseDate = new Date();
    const referenceDate = isWeekend(baseDate) ? addDays(baseDate, 7) : baseDate;

    return {
      dateFrom: startOfWeek(referenceDate, { weekStartsOn }),
      dateTo: endOfWeek(referenceDate, { weekStartsOn }),
    };
  });

  const { dateFrom, dateTo } = dateRange;

  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [debouncedDates, setDebouncedDates] = useState({ dateFrom, dateTo });

  const handleDateChange = (newDateFrom: Date, newDateTo: Date, selectedDate?: Date) => {
    const nextSelectedDayIndex = selectedDate
      ? differenceInCalendarDays(startOfDay(selectedDate), startOfDay(newDateFrom))
      : 0;

    setSelectedDayIndex(
      nextSelectedDayIndex >= 0 && nextSelectedDayIndex <= 6 ? nextSelectedDayIndex : 0,
    );
    setDateRange({ dateFrom: newDateFrom, dateTo: newDateTo });
  };

  useEffect(() => {
    const debouncedUpdateDates = debounce(() => {
      setDebouncedDates({ dateFrom, dateTo });
    }, 500);

    debouncedUpdateDates();
    return () => {
      debouncedUpdateDates.cancel();
    };
  }, [dateFrom, dateTo]);

  const fetchReservationSlotsData = async () => {
    return await fetchReservationSlots({
      locale,
      body: {
        date_from: debouncedDates.dateFrom.toDateString(),
        date_to: debouncedDates.dateTo.toDateString(),
        free_slots: true,
        reserved_slots: true,
        break_slots: true,
        nonreservable_slots: true,
      },
    });
  };

  const isWaitingForDebouncedDates =
    dateFrom.getTime() !== debouncedDates.dateFrom.getTime() ||
    dateTo.getTime() !== debouncedDates.dateTo.getTime();

  const { data: slots, isFetching: isSlotsFetching } = useQuery({
    queryKey: [reservationPersonId, reservationServiceId, debouncedDates.dateFrom],
    queryFn: fetchReservationSlotsData,
    enabled: !!debouncedDates.dateFrom && !!debouncedDates.dateTo,
  });

  const dayDates = useMemo(() => {
    return Array.from({ length: 7 }).map((_, index) =>
      startOfDay(addDays(debouncedDates.dateFrom, index)),
    );
  }, [debouncedDates.dateFrom]);

  const daysOfWeek = useMemo(() => dayDates.map(d => format(d, 'd.M.')), [dayDates]);

  // When the fetched week changes (debouncedDates), ensure we highlight "today" if it exists in that week.
  useEffect(() => {
    const idx = differenceInCalendarDays(today, startOfDay(debouncedDates.dateFrom));
    setSelectedDayIndex(idx >= 0 && idx <= 6 ? idx : 0);
  }, [debouncedDates.dateFrom, today]);

  const selectedDaySlots = useMemo<ReservationSlot[]>(() => {
    const selectedDay = dayDates[selectedDayIndex];
    if (selectedDay) {
      const dayKey = format(selectedDay, 'yyyy-MM-dd');
      if (alwaysOpenKeySet.has(dayKey)) {
        return slots?.[selectedDayIndex] ?? [];
      }
      if (alwaysClosedKeySet.has(dayKey) || holidayKeySet.has(dayKey)) return [];
    }
    return slots?.[selectedDayIndex] ?? [];
  }, [alwaysClosedKeySet, alwaysOpenKeySet, dayDates, holidayKeySet, slots, selectedDayIndex]);

  const selectedDayFreeSlots = useMemo(
    () => selectedDaySlots.filter(slot => slot.type === ReservationSlotType.FREE),
    [selectedDaySlots],
  );

  const isSlotsLoading = isWaitingForDebouncedDates || isSlotsFetching;
  const showNoSlotsMessage = !isSlotsLoading && slots && selectedDaySlots.length === 0;

  useEffect(() => {
    if (isSlotsLoading || !slots) return;
    if (selectedDayFreeSlots.length > 0) return;

    const firstAvailableDayIndex = slots.findIndex(day =>
      day.some(slot => slot.type === ReservationSlotType.FREE),
    );

    if (firstAvailableDayIndex >= 0 && firstAvailableDayIndex !== selectedDayIndex) {
      setSelectedDayIndex(firstAvailableDayIndex);
    }
  }, [isSlotsLoading, selectedDayFreeSlots.length, selectedDayIndex, slots]);

  return (
    <section
      className={cn('custom-section', 'relative pt-40 sm:pt-40 md:pt-52 lg:pt-40 xl:pt-[120px]')}
    >
      <div className="absolute left-0 top-0 z-[20] h-[100px] w-full bg-white" />
      <Image
        src={'/images/my-vector.png'}
        alt="Aktuálne voľné termíny"
        width={0}
        height={0}
        className="absolute left-0 top-28 z-0 hidden h-auto w-[440px] object-contain object-center sm:block"
        sizes="100vw"
      />
      <div
        className={cn(
          'custom-container',
          'relative z-10 flex h-[682px] flex-col items-start justify-start gap-6 sm:h-[474px] md:flex-row md:justify-between lg:gap-8',
        )}
      >
        <div className="flex w-full min-w-[255px] flex-col items-start justify-start gap-6 md:max-w-[384px] lg:gap-12">
          <div className="flex w-full flex-col items-start justify-start gap-6">
            <Heading
              title="Aktuálne voľné termíny"
              className={{ container: 'w-full max-w-[300px]', title: 'text-balance' }}
            />
            <p className="text-sm font-medium sm:text-base">Prehľad dostupných termínov.</p>
          </div>
          <Button asChild variant="dark" className="hidden sm:flex">
            <Link prefetch={false} href="/rezervacia">
              <Image
                src="/images/calendar.svg"
                alt=""
                width={0}
                height={0}
                className="max-h-5 min-h-5 min-w-5 max-w-5 object-contain"
              />
              {t('text4069')}
            </Link>
          </Button>
        </div>

        <div
          className={cn(
            'flex w-full flex-col items-start justify-start gap-6 rounded-[24px] bg-primary-foreground p-4 sm:p-5 md:max-w-[673px] lg:p-6',
            'ea-free-slots__datepicker-wrapper',
          )}
        >
          <ReservationDatepicker
            dateFrom={dateFrom}
            dateTo={dateTo}
            selectedDate={startOfDay(addDays(dateFrom, selectedDayIndex))}
            onDateChange={handleDateChange}
          />

          <div className="hide-scroll-bar flex w-full flex-nowrap items-center justify-start gap-2 overflow-x-scroll">
            {daysOfWeek.map((day, index) => {
              const isPast = isBefore(dayDates[index], today);
              const dayKey = format(dayDates[index], 'yyyy-MM-dd');
              const isAlwaysOpen = alwaysOpenKeySet.has(dayKey);
              const isHoliday = !isAlwaysOpen && holidayKeySet.has(dayKey);
              const isAlwaysClosed = alwaysClosedKeySet.has(dayKey);
              const hasFreeSlots = !!slots?.[index]?.some(s => s.type === ReservationSlotType.FREE);
              const isDisabled =
                isPast || isAlwaysClosed || isHoliday || (!!slots && !hasFreeSlots && !isAlwaysOpen);
              return (
                <Button
                  key={index}
                  variant="none"
                  size="none"
                  type="button"
                  disabled={isDisabled}
                  onClick={() => setSelectedDayIndex(index)}
                  className={cn(
                    'flex h-[38px] items-center justify-center whitespace-nowrap rounded-full bg-white px-4 text-sm font-medium text-black sm:text-base',
                    selectedDayIndex === index && 'bg-black text-white',
                    isDisabled && 'cursor-not-allowed opacity-50',
                  )}
                >
                  {day}
                </Button>
              );
            })}
          </div>

          <div className="flex min-h-[72px] w-full flex-wrap items-start justify-start gap-2">
            {isSlotsLoading && (
              <div className="flex min-h-[72px] w-full items-center justify-center">
                <Spinner className="h-8 w-8 text-black" />
              </div>
            )}

            {!isSlotsLoading &&
              selectedDaySlots.map((slot, index) => (
                <Button
                  asChild
                  key={index}
                  type="button"
                  className={cn(
                    'h-[36px] w-[60px] cursor-pointer rounded-[8px] border border-black/10 py-0 text-center text-sm font-medium sm:w-[90px]',
                    'ea-reservation-calendar-slots__slot',
                    slot.type === ReservationSlotType.FREE &&
                      'ea-reservation-calendar-slots__free bg-transparent text-black hover:bg-black/5',
                    slot.type === ReservationSlotType.BREAK &&
                      'ea-reservation-calendar-slots__break bg-yellow-500 text-yellow-300 hover:bg-yellow-500',
                    slot.type === ReservationSlotType.RESERVED &&
                      'ea-reservation-calendar-slots__reserved bg-destructive text-destructive-foreground hover:bg-destructive',
                    slot.type === ReservationSlotType.NONRESERVABLE &&
                      'ea-reservation-calendar-slots__nonreservable bg-black/50 text-white hover:bg-black/50',
                  )}
                >
                  <Link
                    prefetch={false}
                    href={`/rezervacia?reserved_at=${encodeURIComponent(String(slot.datetime_start))}`}
                  >
                    {format(new Date(slot.datetime_start), 'HH:mm')}
                  </Link>
                </Button>
              ))}

            {showNoSlotsMessage && (
              <p className="flex min-h-[72px] w-full items-center justify-center rounded-[12px] bg-white px-4 text-center text-sm font-medium text-black/60 sm:text-base">
                Pre zvolený deň nie sú dostupné žiadne voľné termíny.
              </p>
            )}
          </div>
        </div>

        <Button asChild variant="dark" className="flex w-full sm:hidden">
          <Link prefetch={false} href="/rezervacia">
            <Image
              src="/images/calendar.svg"
              alt=""
              width={0}
              height={0}
              className="max-h-5 min-h-5 min-w-5 max-w-5 object-contain"
            />
            {t('text4069')}
          </Link>
        </Button>
      </div>
    </section>
  );
}
